import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, LoginCredentials, RegisterData, AuthResponse } from '../types/saas';
import saasApiService from '../services/saasApi';

interface SaasAuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const SaasAuthContext = createContext<SaasAuthContextType | undefined>(undefined);

export const useSaasAuth = () => {
  const context = useContext(SaasAuthContext);
  if (context === undefined) {
    throw new Error('useSaasAuth must be used within a SaasAuthProvider');
  }
  return context;
};

interface SaasAuthProviderProps {
  children: ReactNode;
}

export const SaasAuthProvider: React.FC<SaasAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!token;

  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem('saas_token');
        const storedUser = localStorage.getItem('saas_user');

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          
          // Verificar que el token siga siendo válido
          try {
            const currentUser = await saasApiService.getProfile();
            setUser(currentUser);
            localStorage.setItem('saas_user', JSON.stringify(currentUser));
          } catch (error) {
            // Token inválido, limpiar datos
            localStorage.removeItem('saas_token');
            localStorage.removeItem('saas_user');
            setToken(null);
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Error initializing SaaS auth:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const response: AuthResponse = await saasApiService.login(credentials);
      
      setUser(response.user);
      setToken(response.token);
      
      localStorage.setItem('saas_token', response.token);
      localStorage.setItem('saas_user', JSON.stringify(response.user));
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      const response: AuthResponse = await saasApiService.register(userData);
      
      setUser(response.user);
      setToken(response.token);
      
      localStorage.setItem('saas_token', response.token);
      localStorage.setItem('saas_user', JSON.stringify(response.user));
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await saasApiService.logout();
    } catch (error) {
      console.error('Error during SaaS logout:', error);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem('saas_token');
      localStorage.removeItem('saas_user');
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('saas_user', JSON.stringify(updatedUser));
    }
  };

  const value: SaasAuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
  };

  return (
    <SaasAuthContext.Provider value={value}>
      {children}
    </SaasAuthContext.Provider>
  );
};
